import {
  InternalOrganization,
  InternalUser,
  OrganizationCreate,
  OrganizationUpdate,
  OrganizationUserCreate,
  OrphanedUser,
  UserSettings,
} from '../features/organizations/types/Organizations'
import { StoredAfterWrite } from '../features/plans/types/Plans'
import { internalAccountApi } from './internal-account'
import { internalApiserverApi } from './internal-apiserver'

const organizationsList = { url: 'orgs', params: { limit: 0 } }
const orphansList = 'users/list/orphan'
const usersList = 'users/list/all'
const organizationUsersList = (organizationId: string) => `orgs/${organizationId}/users`
const encodeUsername = (username: string) => encodeURIComponent(username)

const findOrganization = (organizations: unknown, organizationId?: string) =>
  organizationId ? (organizations as InternalOrganization[]).find(({ id }) => id === organizationId) : undefined

const findUser = (users: unknown, matches: (user: InternalUser) => boolean) => (users as InternalUser[]).find(matches)

export type OrganizationUserAdd = {
  user: OrganizationUserCreate
  /** Without an invite the user is added with `?sendMail=false` and the password from the dialog. */
  sendInvite: boolean
  admin: boolean
}

export type OrganizationUserUpdate = {
  organizationId: string
  /** The complete stored user with the dialog's fields, as legacy sends it. */
  user: InternalUser
  /** Set only when the admin checkbox was changed. */
  admin?: boolean
}

export type OrganizationUserReference = { organizationId: string; username: string }

export type InternalUserMove = OrganizationUserReference & { fromOrganizationId: string }

/** Every user write changes the organization's users, its user count and the all-users list. */
const organizationUserTags = (organizationId: string) => [
  { type: 'internalOrganizationUsers' as const, id: organizationId },
  'internalOrganizations' as const,
  'internalUsers' as const,
]

const organizationsApi = internalAccountApi
  .enhanceEndpoints({ addTagTypes: ['internalOrganizations', 'internalOrphanedUsers', 'internalOrganizationUsers', 'internalUsers'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getInternalOrganizations: builder.query<InternalOrganization[], void>({
        query: () => organizationsList,
        providesTags: ['internalOrganizations'],
      }),
      // Answers with the created organization, so it is found by its new id.
      createInternalOrganization: builder.mutation<StoredAfterWrite<InternalOrganization>, OrganizationCreate>({
        async queryFn(organization, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({ url: 'orgs', method: 'POST', body: organization })
          if (saved.error) return { error: saved.error }
          const organizations = await baseQuery(organizationsList)
          if (organizations.error) return { error: organizations.error }
          return { data: { stored: findOrganization(organizations.data, (saved.data as InternalOrganization | null)?.id) } }
        },
        invalidatesTags: ['internalOrganizations'],
      }),
      // Replaces the stored organization with the one sent and answers with an empty body. The backend recomputes the limits from the packages.
      updateInternalOrganization: builder.mutation<StoredAfterWrite<InternalOrganization>, OrganizationUpdate>({
        async queryFn(organization, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({ url: 'orgs', method: 'PUT', body: organization, responseHandler: 'text' })
          if (saved.error) return { error: saved.error }
          const organizations = await baseQuery(organizationsList)
          if (organizations.error) return { error: organizations.error }
          return { data: { stored: findOrganization(organizations.data, organization.id) } }
        },
        invalidatesTags: ['internalOrganizations'],
      }),
      deleteInternalOrganization: builder.mutation<StoredAfterWrite<InternalOrganization>, string>({
        async queryFn(organizationId, _api, _extraOptions, baseQuery) {
          const deleted = await baseQuery({ url: `orgs/${organizationId}`, method: 'DELETE', responseHandler: 'text' })
          if (deleted.error) return { error: deleted.error }
          const organizations = await baseQuery(organizationsList)
          if (organizations.error) return { error: organizations.error }
          return { data: { stored: findOrganization(organizations.data, organizationId) } }
        },
        invalidatesTags: ['internalOrganizations'],
      }),
      getInternalOrphanedUsers: builder.query<OrphanedUser[], void>({
        query: () => orphansList,
        providesTags: ['internalOrphanedUsers'],
      }),
      getInternalOrganizationUsers: builder.query<InternalUser[], string>({
        query: organizationUsersList,
        providesTags: (_result, _error, organizationId) => [{ type: 'internalOrganizationUsers', id: organizationId }],
      }),
      /** Every user, orphans included (with `organization: ''`). */
      getInternalUsers: builder.query<InternalUser[], void>({
        query: () => usersList,
        providesTags: ['internalUsers'],
      }),
      // Answers with the moved user, whom the backend always stores active and without organization admin; a 409 means they're already a member.
      moveInternalUser: builder.mutation<StoredAfterWrite<InternalUser>, InternalUserMove>({
        async queryFn({ organizationId, username }, _api, _extraOptions, baseQuery) {
          const moved = await baseQuery({
            url: `orgs/${organizationId}/users/${encodeUsername(username)}/add`,
            method: 'PUT',
            body: { username, organizationId },
            headers: { Accept: 'application/json' },
            responseHandler: 'text',
          })
          if (moved.error) return { error: moved.error }
          const users = await baseQuery(usersList)
          if (users.error) return { error: users.error }
          return { data: { stored: findUser(users.data, (stored) => stored.username === username) } }
        },
        invalidatesTags: (_result, _error, { organizationId, fromOrganizationId }) => [
          ...organizationUserTags(organizationId),
          { type: 'internalOrganizationUsers', id: fromOrganizationId },
        ],
      }),
      // Answers with the created user. The admin flag is a separate call, made like legacy only after the user exists.
      addInternalOrganizationUser: builder.mutation<StoredAfterWrite<InternalUser>, OrganizationUserAdd>({
        async queryFn({ user, sendInvite, admin }, _api, _extraOptions, baseQuery) {
          const added = await baseQuery({ url: `orgs/${user.id}/users/add`, method: 'POST', body: user, params: sendInvite ? undefined : { sendMail: false } })
          if (added.error) return { error: added.error }
          const created = added.data as InternalUser | null
          const username = created?.username ?? user.email
          if (admin) {
            await baseQuery({
              url: `orgs/${user.id}/admin/${encodeUsername(username)}`,
              method: 'PUT',
              body: { id: user.id, usernames: username },
              responseHandler: 'text',
            })
          }
          const users = await baseQuery(organizationUsersList(user.id))
          if (users.error) return { error: users.error }
          return { data: { stored: findUser(users.data, (stored) => (created?.id ? stored.id === created.id : stored.username === username)) } }
        },
        invalidatesTags: (_result, _error, { user }) => organizationUserTags(user.id),
      }),
      // `PUT users` answers with an empty body; a failed admin call shows up in the read-back.
      updateInternalOrganizationUser: builder.mutation<StoredAfterWrite<InternalUser>, OrganizationUserUpdate>({
        async queryFn({ organizationId, user, admin }, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({ url: 'users', method: 'PUT', body: user, responseHandler: 'text' })
          if (saved.error) return { error: saved.error }
          if (admin !== undefined) {
            await baseQuery({
              url: `orgs/${organizationId}/admin/${encodeUsername(user.username)}`,
              method: admin ? 'PUT' : 'DELETE',
              body: { id: organizationId, usernames: user.username },
              responseHandler: 'text',
            })
          }
          const users = await baseQuery(organizationUsersList(organizationId))
          if (users.error) return { error: users.error }
          return { data: { stored: findUser(users.data, ({ id }) => id === user.id) } }
        },
        invalidatesTags: (_result, _error, { organizationId }) => organizationUserTags(organizationId),
      }),
      // A 409 means the organization has no seats left.
      setInternalOrganizationUserActive: builder.mutation<StoredAfterWrite<InternalUser>, OrganizationUserReference & { active: boolean }>({
        async queryFn({ organizationId, username, active }, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({
            url: `users/${encodeUsername(username)}/${active ? 'enable' : 'disable'}`,
            method: 'PUT',
            body: { username },
            responseHandler: 'text',
          })
          if (saved.error) return { error: saved.error }
          const users = await baseQuery(organizationUsersList(organizationId))
          if (users.error) return { error: users.error }
          return { data: { stored: findUser(users.data, (stored) => stored.username === username) } }
        },
        invalidatesTags: (_result, _error, { organizationId }) => organizationUserTags(organizationId),
      }),
      /** Sets a new password and e-mails the user an invitation with it. Nothing to read back. */
      resetInternalUserPassword: builder.mutation<null, { username: string; password: string }>({
        query: (body) => ({ url: 'users/sendmail', method: 'POST', body, responseHandler: 'text' }),
      }),
      removeInternalOrganizationUser: builder.mutation<StoredAfterWrite<InternalUser>, OrganizationUserReference>({
        async queryFn({ organizationId, username }, _api, _extraOptions, baseQuery) {
          const removed = await baseQuery({
            url: `orgs/${organizationId}/users/${encodeUsername(username)}/remove`,
            method: 'PUT',
            body: { id: organizationId, username },
            responseHandler: 'text',
          })
          if (removed.error) return { error: removed.error }
          const users = await baseQuery(organizationUsersList(organizationId))
          if (users.error) return { error: users.error }
          return { data: { stored: findUser(users.data, (stored) => stored.username === username) } }
        },
        invalidatesTags: (_result, _error, { organizationId }) => [...organizationUserTags(organizationId), 'internalOrphanedUsers'],
      }),
      // Returns a user to `removedFrom`; a 409 means the organization has no seats left. `stored` is the user if still an orphan.
      restoreInternalOrphanedUser: builder.mutation<StoredAfterWrite<OrphanedUser>, OrganizationUserReference>({
        async queryFn({ organizationId, username }, _api, _extraOptions, baseQuery) {
          const restored = await baseQuery({
            url: `orgs/${organizationId}/users/${encodeUsername(username)}/return`,
            method: 'PUT',
            body: { username, organizationId },
            responseHandler: 'text',
          })
          if (restored.error) return { error: restored.error }
          const orphans = await baseQuery(orphansList)
          if (orphans.error) return { error: orphans.error }
          return { data: { stored: findUser(orphans.data, (stored) => stored.username === username) } }
        },
        invalidatesTags: (_result, _error, { organizationId }) => [...organizationUserTags(organizationId), 'internalOrphanedUsers'],
      }),
      /** Deletes the user permanently. `stored` is the user if still listed as an orphan. */
      deleteInternalOrphanedUser: builder.mutation<StoredAfterWrite<OrphanedUser>, string>({
        async queryFn(username, _api, _extraOptions, baseQuery) {
          const deleted = await baseQuery({ url: `users/${encodeUsername(username)}/delete`, method: 'DELETE', responseHandler: 'text' })
          if (deleted.error) return { error: deleted.error }
          const orphans = await baseQuery(orphansList)
          if (orphans.error) return { error: orphans.error }
          return { data: { stored: findUser(orphans.data, (stored) => stored.username === username) } }
        },
        invalidatesTags: ['internalOrphanedUsers'],
      }),
    }),
  })

const organizationGamesApi = internalApiserverApi.enhanceEndpoints({ addTagTypes: ['internalUserSettings'] }).injectEndpoints({
  endpoints: (builder) => ({
    /** The number of distinct games the organization's users have in their game slots. */
    getInternalOrganizationGamesCount: builder.query<number, string>({
      query: (organizationId) => `i/games/count/${organizationId}`,
    }),
    getInternalUserSettings: builder.query<UserSettings, string>({
      query: (username) => `i/users/settings/${encodeUsername(username)}`,
      providesTags: (_result, _error, username) => [{ type: 'internalUserSettings', id: username }],
    }),
    // The backend merges the sent keys into the stored settings.
    updateInternalUserSettings: builder.mutation<StoredAfterWrite<UserSettings>, { username: string; settings: UserSettings }>({
      async queryFn({ username, settings }, _api, _extraOptions, baseQuery) {
        const saved = await baseQuery({ url: 'i/users/settings', method: 'PUT', body: { username, settings }, responseHandler: 'text' })
        if (saved.error) return { error: saved.error }
        const stored = await baseQuery(`i/users/settings/${encodeUsername(username)}`)
        if (stored.error) return { error: stored.error }
        return { data: { stored: stored.data as UserSettings } }
      },
      invalidatesTags: (_result, _error, { username }) => [{ type: 'internalUserSettings', id: username }],
    }),
  }),
})

export const {
  useGetInternalOrganizationsQuery,
  useCreateInternalOrganizationMutation,
  useUpdateInternalOrganizationMutation,
  useDeleteInternalOrganizationMutation,
  useGetInternalOrphanedUsersQuery,
  useGetInternalOrganizationUsersQuery,
  useLazyGetInternalOrganizationUsersQuery,
  useGetInternalUsersQuery,
  useMoveInternalUserMutation,
  useAddInternalOrganizationUserMutation,
  useUpdateInternalOrganizationUserMutation,
  useSetInternalOrganizationUserActiveMutation,
  useResetInternalUserPasswordMutation,
  useRemoveInternalOrganizationUserMutation,
  useRestoreInternalOrphanedUserMutation,
  useDeleteInternalOrphanedUserMutation,
} = organizationsApi

export const { useLazyGetInternalOrganizationGamesCountQuery, useGetInternalUserSettingsQuery, useUpdateInternalUserSettingsMutation } = organizationGamesApi
